import autoTable from "jspdf-autotable";
import { convertAmountToWords } from "./NumberWordHelper";
import dayjs from "dayjs";

const SaudiArabicBilingualInvoice = (
  doc,
  pageWidth,
  pageHeight,
  invoiceData,
  REPORT_FOOTER_IMAGE,
  REPORT_HEADER_IMAGE,
  COMPANY_NAME,
  qrCodeImage = null,
  ARABIC_FONT = null,
) => {
  if (!invoiceData || invoiceData.length === 0) return;

  // --- Font Setup ---
  if (ARABIC_FONT) {
    doc.addFileToVFS("Amiri-Regular.ttf", ARABIC_FONT);
    doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
  }

  const firstItem = invoiceData[0];
  const margin = 10;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  // --- Helper: Draw Header ---
  const drawHeader = () => {
    const headerHeight = 35;
    if (REPORT_HEADER_IMAGE) {
      doc.addImage(
        REPORT_HEADER_IMAGE,
        "PNG",
        margin,
        margin,
        contentWidth,
        headerHeight,
      );
    }
    return margin + headerHeight + 5;
  };

  yPosition = drawHeader();

  // --- 2. Title & VAT/Contact Info ---
  const titleBoxHeight = 15;
  doc.rect(margin, yPosition, contentWidth, titleBoxHeight); // Title Box

  // 2a. Print Title (Left Aligned)
  if (ARABIC_FONT) {
    doc.setFont("Amiri", "normal");
  } else {
    doc.setFont("helvetica", "bold");
  }
  doc.setFontSize(14);
  // Center vertically: yPosition + (height / 2) + offset
  doc.text("TAX INVOICE - فاتورة ضريبية", margin + 5, yPosition + 10);

  // 2b. Print VAT NO (Right Aligned)
  // Positioned at yPosition + 10 to stay on the same baseline as the title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10); // Slightly increased size since there is more room now
  doc.text(`VAT NO : 311580444700003`, pageWidth - margin - 5, yPosition + 10, {
    align: "right",
  });

  yPosition += titleBoxHeight; // Standardized increment

  // --- 3. Information Grid ---
  const gridHeight = 42;
  const cellHeightRight = 14; // 42 / 3 = 14 (Equal height for Customer, Address, TRN)
  const cellHeightLeft = 7; // 42 / 6 = 7

  // Draw main outer box and vertical split
  doc.setDrawColor(0);
  doc.rect(margin, yPosition, contentWidth, gridHeight);
  doc.line(pageWidth / 2, yPosition, pageWidth / 2, yPosition + gridHeight);

  const drawGridCell = (x, y, w, h, labelEn, labelAr, value) => {
    // 1. Labels (English Left, Arabic Right)
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold"); // Labels stay bold
    doc.text(labelEn, x + 2, y + h / 2 + 1.5, { baseline: "middle" });

    doc.setFont("Amiri", "normal");
    doc.text(labelAr, x + w - 2, y + h / 2 + 1.5, {
      align: "right",
      baseline: "middle",
    });

    // 2. Central Value - CHANGED TO BOLD HERE
    doc.setFont("helvetica", "bold"); // <--- Change "normal" to "bold"
    doc.setFontSize(8);

    const valWidth = w - 40;
    const splitValue = doc.splitTextToSize(String(value || ""), valWidth);

    doc.text(splitValue, x + w / 2, y + h / 2 + 1, {
      align: "center",
      baseline: "middle",
    });

    // 3. Internal Border Line
    if (y + h < yPosition + gridHeight) {
      doc.line(x, y + h, x + w, y + h);
    }
  };

  const colW = contentWidth / 2;

  // --- Left Column (6 Rows) ---
  const leftLabels = [
    ["INVOICE NO", "رقم الفاتورة", firstItem.inv_no],
    ["DATE", "تاريخ الفاتورة", dayjs(firstItem.inv_date).format("DD-MM-YYYY")],
    ["LPO NO", "رقم أمر الشراء", firstItem.lpo_no],
    ["DO NO", "رقم إشعار التسليم", firstItem.do_data],
    ["SALESMAN", "رجل المبيعات", firstItem.sale_man],
    ["PROJECT", "مشروع", firstItem.insurance],
  ];

  leftLabels.forEach((item, i) => {
    drawGridCell(
      margin,
      yPosition + i * cellHeightLeft,
      colW,
      cellHeightLeft,
      item[0],
      item[1],
      item[2],
      true,
    );
  });

  // --- Right Column (3 Rows - Equal Height) ---
  const rightX = pageWidth / 2;

  drawGridCell(
    rightX,
    yPosition,
    colW,
    cellHeightRight,
    "CUSTOMER",
    "عميل",
    firstItem.custref,
    true,
  );
  drawGridCell(
    rightX,
    yPosition + cellHeightRight,
    colW,
    cellHeightRight,
    "CITY",
    "عنوان",
    firstItem.address1,
    true,
  );
  drawGridCell(
    rightX,
    yPosition + cellHeightRight * 2,
    colW,
    cellHeightRight,
    "TRN",
    "الرقم الضريبي",
    firstItem.governor,
    true,
  );

  yPosition += gridHeight + 5; // --- 4. Main Items Table (Filtered for FOC) ---
  const printableData = invoiceData.filter((item) => item.unit !== "FOC");

  autoTable(doc, {
    startY: yPosition,
    margin: { left: margin, right: margin },
    tableWidth: contentWidth,
    head: [
      [
        "S.NO\nرقم",
        "ITEM CODE\nرقم الصنف",
        "PRODUCT DESCRIPTION\nوصف المنتج",
        "UNIT\nالوحدة",
        "QTY\nالكمية",
        "PRICE SAR\nسعر",
        "AMOUNT SAR\nمبلغ",
        "VAT\nالضريبة",
      ],
    ],
    body: printableData.map((item, i) => [
      i + 1,
      item.code || "",
      item.idesc || "",
      item.unit || "NOS",
      item.tr_qty || 0,
      (item.funit_pric || 0).toFixed(2),
      (item.tr_qty * item.funit_pric).toFixed(2),
      "15.00%",
    ]),
    theme: "grid",
    styles: {
      font: "Amiri", // Default to Amiri to handle Arabic in headers
      fontSize: 7,
      halign: "center",
      valign: "middle",
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      font: "Amiri",
      fontStyle: "normal",
      fontSize: 7,
    },
    columnStyles: {
      0: { cellWidth: 12, font: "helvetica" }, // S.NO (Narrow)
      1: { cellWidth: 25, font: "helvetica" }, // ITEM CODE
      2: { cellWidth: "auto", halign: "left" }, // DESCRIPTION (Expands)
      3: { cellWidth: 15 }, // UNIT
      4: { cellWidth: 15, font: "helvetica" }, // QTY
      5: { cellWidth: 22, font: "helvetica" }, // PRICE
      6: { cellWidth: 22, font: "helvetica" }, // AMOUNT
      7: { cellWidth: 15, font: "helvetica" }, // VAT
    },
    didParseCell: (data) => {
      // Re-confirm Amiri for Arabic content to prevent "extra character" boxes
      if (data.section === "head" || /[\u0600-\u06FF]/.test(data.cell.text)) {
        data.cell.styles.font = "Amiri";
      } else if (data.section === "body") {
        // Use Helvetica for numbers in body for the clean look in your image
        data.cell.styles.font = "helvetica";
      }
    },
  });

  yPosition = doc.lastAutoTable.finalY + 5;

  // --- 5. Pagination Check & Summary ---
  const summaryHeight = 80; // Estimated height for summary + bank + signatures
  if (yPosition + summaryHeight > pageHeight - 40) {
    doc.addPage();
    yPosition = drawHeader();
  }
  const discountAmount = parseFloat(firstItem.disc_amt || 0);
  const subtotal = printableData.reduce(
    (acc, item) => acc + item.tr_qty * item.funit_pric,
    0,
  );
  // Calculate VAT based on (Subtotal - Discount)
  const taxableAmount = subtotal - discountAmount;
  const vatAmount = taxableAmount * 0.15;
  const grandTotal = taxableAmount + vatAmount;
  const currency = firstItem.fc || "SAR";
  autoTable(doc, {
    startY: yPosition,
    margin: { left: margin, right: margin },
    tableWidth: contentWidth,
    body: [
      ["Subtotal", subtotal.toFixed(2) + ` ${currency}`, "الإجمالي الفرعي"],
      ["Discount", discountAmount.toFixed(2) + ` ${currency}`, "الخصم"],
      ["VAT 15%", vatAmount.toFixed(2) + ` ${currency}`, "قيمة الضريبة"],
      ["Grand Total", grandTotal.toFixed(2) + ` ${currency}`, "المجموع الكلي"],
    ],
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 2,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
    },
    columnStyles: {
      0: {
        fillColor: [240, 240, 240],
        fontStyle: "bold",
        halign: "left",
        cellWidth: 50,
      },
      1: { halign: "center", fontStyle: "bold" },
      2: {
        fillColor: [240, 240, 240],
        font: "Amiri",
        halign: "right",
        cellWidth: 50,
      },
    },
  });

  yPosition = doc.lastAutoTable.finalY + 8;

  // --- 6. Bank Info, QR, and Amount Words ---
  const textWidth = contentWidth - 45;
  const qrSize = 30;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const amountWords = `Amount Chargeable (in words): ${convertAmountToWords(grandTotal, firstItem.fc || "SAR").toUpperCase()}`;
  const splitWords = doc.splitTextToSize(amountWords, textWidth);
  doc.text(splitWords, margin, yPosition);

  const bankY = yPosition + splitWords.length * 4 + 2;
  doc.setFont("helvetica", "normal");
  doc.text("Bank Name : ALRAJHI BANK", margin, bankY);
  doc.text("IBAN : SA5780000458608015855007", margin, bankY + 5);
  doc.text("Account Number : 458000010006085855007", margin, bankY + 10);

  if (qrCodeImage) {
    doc.addImage(
      qrCodeImage,
      "PNG",
      pageWidth - margin - qrSize,
      yPosition - 2,
      qrSize,
      qrSize,
    );
  }

  // --- 7. Signatures & Footer ---
  const footerTop = pageHeight - 35;
  const sigY = footerTop - 15;

  // Line for Receiver
  doc.line(margin, sigY, margin + 50, sigY);
  doc.setFont("helvetica", "normal");
  doc.text("Received By", margin + 25, sigY + 5, { align: "center" });

  // Line for Authorised Signature
  const authSignX = pageWidth - margin - 60;
  doc.line(authSignX, sigY, pageWidth - margin, sigY);
  doc.setFont("helvetica", "bold");
  doc.text(`For ${COMPANY_NAME}`, authSignX + 30, sigY + 5, {
    align: "center",
  });

  if (REPORT_FOOTER_IMAGE) {
    doc.addImage(
      REPORT_FOOTER_IMAGE,
      "PNG",
      margin,
      footerTop,
      contentWidth,
      20,
    );
  }
};

export default SaudiArabicBilingualInvoice;
